import React, { useState, useEffect, lazy } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Briefcase, Clock, AlertCircle, ArrowLeft, MapPin, Building2, ExternalLink, Heart, Globe, Mail } from 'lucide-react';

import BrandLogo from '../../components/ui/BrandLogo';
import Button from '../../components/ui/Button';
import ThemeToggle from '../../components/ui/ThemeToggle';
import Card from '../../components/ui/Card';
import Chip from '../../components/ui/Chip';
import AppImage from '../../components/AppImage';
import Footer from '../../components/ui/Footer';
import { LazyLoadBoundary } from '../../components/LazyLoadBoundary';

// Lazy load heavy job detail components
const JobTabs = lazy(() => import('./components/JobTabs'));
const JobRightSidebar = lazy(() => import('./components/JobRightSidebar'));
const ApplicationModal = lazy(() => import('./components/ApplicationModal'));



import { useAuth } from '../../contexts/AuthContext';
import { toggleSavedJob } from '../../services/savedJobsService';
import { trackJobInteraction } from '../../services/jobRecommendationsService';
import { getJobById } from '../../services/jobSearchService';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
  hover: { y: -4, transition: { duration: 0.2 } }
};

const JobDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const authContext = useAuth();
  const { user = null, userProfile = null } = authContext || {};

  const [job, setJob] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [showUnauthModal, setShowUnauthModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [isFollowing, setIsFollowing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const isLoggedIn = !!user;

  useEffect(() => {
    loadJobDetails();
  }, [id]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabFromUrl = params?.get('tab');
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, []);

  const loadJobDetails = async () => {
    try {
      setLoading(true);
      const jobData = await getJobById(id);
      
      if (!jobData) {
        navigate('/404-error-page');
        return;
      }

      setJob(jobData);
      setCompany(jobData?.companies);

      // Check if job is saved
      if (user?.id) {
        try {
          const savedJobsService = await import('../../services/savedJobsService');
          const savedJob = await savedJobsService?.checkIfJobSaved(user?.id, id);
          setIsSaved(savedJob);
        } catch (error) {
          console.error('Error checking saved job:', error);
        }
      }

      // Track job view
      if (user?.id) {
        await trackJobInteraction(id, 'view');
      }
    } catch (error) {
      console.error('Error loading job details:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check if job is expiring soon (within 7 days)
  const isExpiringSoon = () => {
    if (!job?.locations?.length) return false;
    
    const now = new Date();
    return job?.locations?.some(location => {
      if (!location?.validThrough || location?.status === 'CLOSED') return false;
      const expirationDate = new Date(location?.validThrough);
      const diffTime = expirationDate - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays <= 7 && diffDays > 0;
    });
  };

  // Check if job is expired or all locations are closed
  const isExpired = () => {
    if (!job?.locations?.length) return false;
    
    const now = new Date();
    
    // Check job-level status first
    if (job?.status === 'CLOSED') return true;
    
    return job?.locations?.every(location => {
      if (location?.status === 'CLOSED') return true;
      if (!location?.validThrough) return false;
      return new Date(location?.validThrough) < now;
    });
  };

  // Get available locations for application
  const getAvailableLocations = () => {
    if (!job?.locations?.length) return [];
    
    const now = new Date();
    
    // If job is closed at job level, no locations are available
    if (job?.status === 'CLOSED') return [];
    
    return job?.locations?.filter(location => {
      if (location?.status === 'CLOSED') return false;
      if (!location?.validThrough) return true;
      return new Date(location?.validThrough) >= now;
    });
  };

  // Determine CTA based on job status
  const getJobCTA = () => {
    const availableLocations = getAvailableLocations();
    if (job?.status === 'CLOSED' || availableLocations?.length === 0) {
      return 'disabled_apply';
    }
    return 'apply';
  };

  const handleSearch = () => {
    if (searchQuery?.trim()) {
      navigate(`/jobs?q=${encodeURIComponent(searchQuery?.trim())}`);
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter') {
      handleSearch();
    }
  };

  const handleFollowJob = () => {
    // Mock authentication check
    const isAuthenticated = false;
    
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    setIsFollowing(!isFollowing);
    // In real app: call API to follow/unfollow job
  };

  const handleSaveJob = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      const result = await toggleSavedJob(user?.id, id);
      
      if (result?.success) {
        setIsSaved(result?.action === 'saved');
        
        // Track interaction
        await trackJobInteraction(id, result?.action, {
          source: 'job_details_page'
        });
      }
    } catch (error) {
      console.error('Error saving job:', error);
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const params = new URLSearchParams(window.location.search);
    params?.set('tab', tabId);
    const newUrl = `${window.location?.pathname}?${params?.toString()}`;
    window.history?.replaceState({}, '', newUrl);
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-950">
        {/* Loading Header Skeleton */}
        <header className="bg-white dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800 sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 bg-gray-200 dark:bg-gray-800 rounded animate-pulse"></div>
                <div className="h-6 border-l border-gray-200 dark:border-gray-800"></div>
                <div className="w-24 h-6 bg-gray-200 dark:bg-gray-800 rounded animate-pulse"></div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 bg-gray-200 dark:bg-gray-800 rounded animate-pulse"></div>
                <div className="w-16 h-8 bg-gray-200 dark:bg-gray-800 rounded animate-pulse"></div>
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="space-y-6">
            {/* Hero card skeleton */}
            <Card className="p-8">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 bg-gray-200 dark:bg-gray-800 rounded-xl animate-pulse"></div>
                <div className="flex-1">
                  <div className="w-64 h-8 bg-gray-200 dark:bg-gray-800 rounded mb-2 animate-pulse"></div>
                  <div className="w-48 h-6 bg-gray-200 dark:bg-gray-800 rounded mb-3 animate-pulse"></div>
                  <div className="flex gap-2">
                    <div className="w-16 h-6 bg-gray-200 dark:bg-gray-800 rounded animate-pulse"></div>
                    <div className="w-16 h-6 bg-gray-200 dark:bg-gray-800 rounded animate-pulse"></div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Show 404 if job not found
  if (!job) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <Briefcase className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Job Not Found</h1>
          <p className="text-gray-600 dark:text-gray-300">The job posting you're looking for doesn't exist or may have been removed.</p>
          <Button 
            onClick={() => navigate('/jobs')}
            className="mt-4"
          >
            Browse Jobs
          </Button>
        </div>
      </div>
    );
  }

  // Generate SEO structured data for JobPosting (only for ACTIVE and OPEN jobs)
  const generateJobPostingSchema = () => {
    // Only generate schema for Google Jobs compliant active jobs
    if (job?.status !== 'ACTIVE') return null;
    
    const activeOpenLocations = getAvailableLocations();
    if (!activeOpenLocations?.length) return null;

    const baseSalary = job?.salary?.min && job?.salary?.max ? {
      "@type": "MonetaryAmount",
      "currency": job?.salary?.currency || 'USD',
      "value": {
        "@type": "QuantitativeValue",
        "minValue": parseInt(job?.salary?.min),
        "maxValue": parseInt(job?.salary?.max),
        "unitText": job?.salary?.unit?.toUpperCase() || 'YEAR'
      }
    } : undefined;

    const jobLocations = activeOpenLocations?.map(location => ({
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": location?.address?.split(', ')?.[0],
        "addressRegion": location?.address?.split(', ')?.[1],
        "addressCountry": "US"
      }
    }));

    const earliestValidThrough = activeOpenLocations?.length > 0 
      ? new Date(Math.min(...activeOpenLocations?.map(loc => new Date(loc?.validThrough || '9999-12-31').getTime())))?.toISOString()
      : new Date('9999-12-31')?.toISOString();

    return {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      "title": job?.title,
      "description": job?.description,
      "datePosted": job?.postedAt,
      "validThrough": earliestValidThrough,
      "employmentType": job?.employmentType,
      "occupationalCategory": job?.occupationalCategory || job?.title,
      "workHours": job?.workSchedule?.map(ws => ws?.replace('_', ' ')?.toLowerCase())?.join(', ') || job?.workMode,
      "hiringOrganization": {
        "@type": "Organization",
        "name": job?.company?.name,
        "logo": job?.company?.logoUrl,
        "url": job?.company?.websiteUrl
      },
      "jobLocation": jobLocations,
      "baseSalary": baseSalary,
      "qualifications": job?.skills?.join(', ') || '',
      "industry": job?.company?.industry,
      "benefits": Object.entries(job?.benefits || {})
        ?.filter(([key, value]) => value === true && key !== 'customBenefits')
        ?.map(([key]) => key?.replace(/([A-Z])/g, ' $1')?.toLowerCase()?.replace(/^\w/, c => c?.toUpperCase()))
        ?.concat(job?.benefits?.customBenefits || [])
        ?.join(', '),
      "applicationContact": {
        "@type": "ContactPoint",
        "email": job?.applicationDetails?.applicationEmail,
        "url": job?.applicationDetails?.applicationUrl
      }
    };
  };

  const pageTitle = `${job?.title} at ${job?.company?.name} | Recrutees`;
  const pageDescription = `${job?.title} position at ${job?.company?.name}. ${job?.description?.slice(0, 150)}...`;
  const schema = generateJobPostingSchema();

  const handleApplyNow = () => {
    if (!isLoggedIn) {
      navigate('/login', { state: { from: location?.pathname } });
      return;
    }
    
    // Get available locations for the job
    const availableLocations = getAvailableLocations();
    
    // If no available locations, don't open modal
    if (availableLocations?.length === 0) {
      return;
    }
    
    // Set first available location as default
    if (availableLocations?.length > 0 && !selectedLocation) {
      setSelectedLocation(availableLocations?.[0]?.id);
    }
    
    setShowApplicationModal(true);
  };

  const handleApply = () => {
    handleApplyNow();
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={job?.company?.bannerUrl || job?.company?.logoUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={job?.company?.bannerUrl || job?.company?.logoUrl} />
        
        {/* JSON-LD Structured Data for JobPosting - Only for compliant active jobs */}
        {schema && (
          <script type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        )}
      </Helmet>
      <motion.div 
        className="min-h-screen bg-white dark:bg-gray-950"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Global Header - Same as Company Page */}
        <header className="bg-white dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800 sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(-1)}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                
                <div className="h-6 border-l border-gray-200 dark:border-gray-800"></div>
                
                <BrandLogo size="sm" showIcon={true} showText={true} linkTo="/" />
              </div>

              <div className="flex items-center gap-4">
                <ThemeToggle />
                <Button 
                  variant="default" 
                  size="sm"
                  onClick={() => navigate('/login')}
                >
                  Sign in
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Job Status Alerts */}
        {(isExpiringSoon() || isExpired() || job?.status === 'CLOSED' || getAvailableLocations()?.length === 0) && (
          <div className={`border-b ${getAvailableLocations()?.length === 0 ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' : 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800'}`}>
            <div className="max-w-7xl mx-auto px-6 py-3">
              <div className="flex items-center gap-2">
                {getAvailableLocations()?.length === 0 ? (
                  <>
                    <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                    <span className="text-red-600 dark:text-red-400 font-medium">
                      This job is closed
                    </span>
                    <span className="text-red-600/70 dark:text-red-400/70">
                      • All locations are no longer accepting applications
                    </span>
                  </>
                ) : (
                  <>
                    <Clock className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                    <span className="text-orange-600 dark:text-orange-400 font-medium">
                      Some locations closing soon
                    </span>
                    <span className="text-orange-600/70 dark:text-orange-400/70">
                      • Apply quickly to secure your application
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <main>
          {/* Full-width Company Banner */}
          <section className="relative">
            <div className="h-48 lg:h-64 w-full overflow-hidden">
              <AppImage
                src={job?.company?.bannerUrl}
                alt={`${job?.company?.name} banner`}
                className="w-full h-full object-cover"
              />
            </div>
          </section>

          {/* Hero Section - Overlapping card design matching company page */}
          <section className="relative -mt-16 lg:-mt-20 z-10 mb-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
              <motion.div variants={cardVariants} whileHover="hover">
                <Card className="p-8 shadow-xl">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
                    {/* Left: Job & Company Info */}
                    <div className="flex items-start gap-6 flex-1">
                      <div className="w-20 h-20 lg:w-24 lg:h-24 rounded-2xl overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0 shadow-lg">
                        <AppImage
                          src={job?.company?.logoUrl}
                          alt={`${job?.company?.name} logo`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white">
                            {job?.title}
                          </h1>
                        </div>
                        
                        {/* Company link and job metadata */}
                        <div className="flex flex-wrap items-center gap-4 mb-4">
                          <button
                            onClick={() => navigate(`/c/${job?.company?.slug}`)}
                            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-lg group"
                          >
                            <Building2 className="w-5 h-5" />
                            {job?.company?.name}
                            <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </button>
                          
                          {job?.employmentType && (
                            <Chip variant="outline" size="sm">{job?.employmentType}</Chip>
                          )}
                          {job?.experienceLevel && (
                            <Chip variant="outline" size="sm">{job?.experienceLevel}</Chip>
                          )}
                        </div>
                        
                        {/* Location info with links */}
                        <div className="flex flex-wrap items-center gap-4 mb-4">
                          {job?.locations?.map((location, index) => (
                            <button
                              key={index}
                              onClick={() => navigate(`/c/${job?.company?.slug}/locations/${location?.locationId || index}`)}
                              className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 group"
                            >
                              <MapPin className="w-4 h-4" />
                              {location?.address}
                              <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </button>
                          ))}
                        </div>
                        
                        {/* Compensation */}
                        {(job?.compensation || job?.salary) && (
                          <div className="mb-4">
                            <Chip variant="secondary" size="lg" className="bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300">
                              {(() => {
                                if (job?.compensation && typeof job?.compensation === 'object') {
                                  if (job?.compensation?.min && job?.compensation?.max) {
                                    const currency = job?.compensation?.currency || '$';
                                    const unit = job?.compensation?.unit || 'K';
                                    const period = job?.compensation?.period ? ` ${job?.compensation?.period}` : '';
                                    return `${currency}${job?.compensation?.min}${unit} - ${currency}${job?.compensation?.max}${unit}${period}`;
                                  }
                                  return 'Competitive salary';
                                }
                                
                                if (job?.salary && typeof job?.salary === 'string') {
                                  return job?.salary;
                                }
                                
                                return 'Competitive salary';
                              })()}
                            </Chip>
                          </div>
                        )}
                        
                        {/* Key job highlights */}
                        <div className="text-gray-600 dark:text-gray-300 text-sm lg:text-base">
                          <p className="line-clamp-2">
                            {job?.description?.substring(0, 200)}...
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Right: Action Buttons */}
                    <div className="flex flex-col gap-3 lg:min-w-[200px]">
                      {/* Apply Button */}
                      <Button
                        variant="default"
                        size="lg"
                        onClick={handleApplyNow}
                        disabled={getJobCTA() === 'disabled_apply'}
                        className={`flex items-center justify-center gap-2 w-full ${
                          getJobCTA() === 'disabled_apply' ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500': 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        <Briefcase className="h-5 w-5" />
                        {getJobCTA() === 'disabled_apply' ? 'Job Closed' : 'Apply Now'}
                      </Button>
                      
                      {/* Follow Job Button */}
                      <Button
                        variant="outline"
                        size="lg"
                        onClick={handleFollowJob}
                        className="flex items-center justify-center gap-2 w-full"
                      >
                        <Heart className={`h-5 w-5 ${isFollowing ? 'fill-current text-red-500' : ''}`} />
                        {isFollowing ? 'Following' : 'Follow Job'}
                      </Button>
                      
                      {/* Action buttons row */}
                      <div className="flex gap-2">
                        {job?.company?.websiteUrl && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window?.open(job?.company?.websiteUrl, '_blank')}
                            className="flex items-center gap-2 flex-1"
                          >
                            <Globe className="h-4 w-4" />
                            Website
                          </Button>
                        )}
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window?.open(`mailto:${job?.applicationDetails?.applicationEmail}`, '_blank')}
                          className="flex items-center gap-2 flex-1"
                        >
                          <Mail className="h-4 w-4" />
                          Email
                        </Button>
                      </div>
                      
                      {/* Quick stats card */}
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Job Details
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-300">Views</span>
                            <span className="font-semibold text-gray-900 dark:text-white">{job?.viewCount || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-300">Applications</span>
                            <span className="font-semibold text-gray-900 dark:text-white">{job?.applicationCount || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-300">Status</span>
                            <span className={`font-semibold ${job?.status === 'ACTIVE' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                              {job?.status === 'ACTIVE' ? 'Open' : 'Closed'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            </div>
          </section>

          {/* Tabs Bar - Same as company page */}
          <section className="border-b border-gray-200 dark:border-gray-800 sticky top-16 z-30 bg-white/95 dark:bg-gray-950/95 backdrop-blur-sm mb-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <nav className="flex space-x-8 py-4" role="tablist">
                {[
                  { id: 'overview', label: 'Overview' },
                  { id: 'company', label: 'Company' },
                  { id: 'locations', label: 'Locations' }
                ]?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => handleTabChange(tab?.id)}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      activeTab === tab?.id
                        ? 'bg-blue-600 text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                    role="tab"
                    aria-selected={activeTab === tab?.id}
                  >
                    {tab?.label}
                  </button>
                ))}
              </nav>
            </div>
          </section>

          {/* Main Content - Two Column Layout */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Content - Job Details */}
              <div className="lg:col-span-2">
                <JobTabs 
                  activeTab={activeTab} 
                  onTabChange={setActiveTab} 
                  job={job}
                  company={company}
                />
              </div>

              {/* Right Sidebar */}
              <div className="lg:col-span-1">
                <LazyLoadBoundary 
                  errorFallback={<div className="text-red-500">Failed to load sidebar</div>}
                  onError={(error) => console.error('LazyLoadBoundary error:', error)}
                >
                  <JobRightSidebar 
                    job={job}
                    company={company}
                    onApply={handleApply}
                    onSave={handleSaveJob}
                    isSaved={isSaved}
                    isLoggedIn={isLoggedIn}
                  />
                </LazyLoadBoundary>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <motion.footer 
          className="w-full py-8 text-center border-t border-gray-200 dark:border-gray-800 mt-16"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.6 }}
        >
          <p className="text-sm text-gray-500 dark:text-gray-400">
            © 2025 Recrutees · Privacy · Terms
          </p>
        </motion.footer>
      </motion.div>
      {/* Application Modal */}
      {showApplicationModal && (
        <LazyLoadBoundary
          errorFallback={<div className="text-red-500">Failed to load application modal</div>}
          onError={(error) => console.error('LazyLoadBoundary error:', error)}
        >
          <ApplicationModal
            isOpen={showApplicationModal}
            onClose={() => {
              setShowApplicationModal(false);
              setSelectedLocation(null);
            }}
            job={job}
            availableLocations={getAvailableLocations()}
          />
        </LazyLoadBoundary>
      )}
    </>
  );
};

export default JobDetails;